import React, { useState, useEffect } from 'react';
import { PDFDownloadLink, PDFViewer, pdf } from '@react-pdf/renderer';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, IconButton, Tooltip, CircularProgress,
  useMediaQuery, useTheme, Typography, Alert, Snackbar
} from '@mui/material';
import { Close, Download, Print, Visibility, WhatsApp } from '@mui/icons-material';
import EstimatePDF from './EstimatePDF';
import { getCompanyConfig, getCompanyConfigFromAPI } from '../Sale/CompanyConfig';
import { useAuth } from '../../../AuthContext';

// ── Error Boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('PDF Error:', err); }
  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          <Typography variant="h6">PDF Generation Error</Typography>
          <Typography variant="body2">Unable to render PDF preview. Please try again.</Typography>
          <Button variant="outlined" size="small"
            onClick={() => this.setState({ hasError: false })} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Alert>
      );
    }
    return this.props.children;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
const EstimatePDFViewer = ({ open, onClose, estimateData, estimateDetails, companyData }) => {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState('');
  const [success, setSuccess]   = useState('');

  const { token, companyId: authCompanyId, branchId: authBranchId } = useAuth();
  const [fetchedCompanyData,  setFetchedCompanyData]  = useState(null);
  const [companyDataLoading,  setCompanyDataLoading]  = useState(false);

  // ── Fetch company/branch config ────────────────────────────────────────────
  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (companyData) { setFetchedCompanyData(companyData); return; }
      const cId = estimateData?.CompanyId || authCompanyId;
      const bId = estimateData?.BranchId  || authBranchId;
      if (cId && bId && token) {
        setCompanyDataLoading(true);
        try {
          const data = await getCompanyConfigFromAPI(cId, bId, token);
          setFetchedCompanyData(data);
        } catch {
          setFetchedCompanyData(getCompanyConfig());
        } finally {
          setCompanyDataLoading(false);
        }
      } else {
        setFetchedCompanyData(getCompanyConfig());
      }
    };
    if (open && estimateData) fetchIfNeeded();
  }, [open, estimateData, companyData, token, authCompanyId, authBranchId]);

  const finalCompanyData = fetchedCompanyData || getCompanyConfig();
  const safeDetails      = Array.isArray(estimateDetails) ? estimateDetails : [];

  const fileName = `Estimate-${estimateData?.EstimateNo || estimateData?.BillNumber || 'Draft'}-${new Date().toISOString().split('T')[0]}.pdf`;

  // ── Generate PDF blob ──────────────────────────────────────────────────────
  const generatePDFBlob = async () => {
    try {
      setLoading(true);
      setError('');
      const blob = await pdf(
        <EstimatePDF
          estimateData={estimateData}
          estimateDetails={safeDetails}
          companyData={finalCompanyData}
        />
      ).toBlob();
      setLoading(false);
      return blob;
    } catch (err) {
      setError(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
      setLoading(false);
      return null;
    }
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = async () => {
    const blob = await generatePDFBlob();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) {
        win.onload = () => {
          win.print();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        };
      }
    }
  };

  // ── WhatsApp — send PDF directly via Web Share API ───────────────────────
  const handleWhatsApp = async () => {
    const blob = await generatePDFBlob();
    if (!blob) return;

    const companyName = finalCompanyData?.name || '';
    const heading     = [companyName, 'ESTIMATE'].filter(Boolean).join('\n');

    const file = new File([blob], fileName, { type: 'application/pdf' });

    // ── Mobile: Web Share API sends PDF directly into WhatsApp ────────────
    if (navigator.share) {
      try {
        await navigator.share({
          title: heading,
          files: [file]
        });
        return;
      } catch (shareErr) {
        // User cancelled or share failed — fall through to wa.me
        if (shareErr.name === 'AbortError') return; // user cancelled, do nothing
        console.log('Web Share failed, falling back to WhatsApp Web:', shareErr);
      }
    }

    // ── Desktop fallback: download PDF + open WhatsApp with heading only ──
    const pdfUrl = URL.createObjectURL(blob);
    const link   = document.createElement('a');
    link.href     = pdfUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);

    const phone      = (estimateData?.ContactNumber1 || '').replace(/\D/g, '');
    const cleanPhone = phone ? (phone.startsWith('91') ? phone : `91${phone}`) : '';
    const waUrl      = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(heading)}`
      : `https://wa.me/?text=${encodeURIComponent(heading)}`;

    setTimeout(() => window.open(waUrl, '_blank'), 500);
    setSuccess('PDF downloaded. WhatsApp opened — please attach the PDF in the chat.');
  };

  // ── Guard renders ──────────────────────────────────────────────────────────
  if (!estimateData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">No estimate data provided for PDF generation.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  if (companyDataLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">Loading company information...</Typography>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
      </Dialog>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2,
            maxHeight: '95vh',
            width: isMobile ? '100%' : '90%',
            maxWidth: '1200px'
          }
        }}
      >
        {/* ── Title bar ─────────────────────────────────────────────── */}
        <DialogTitle sx={{
          py: 1.5, px: 2,
          borderBottom: '1px solid #e5e7eb',
          bgcolor: '#eff6ff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ color: '#667eea', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#667eea' }}>
              Estimate Preview — {estimateData?.EstimateNo || `EST-${estimateData?.BillNumber}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

            {/* Download */}
            <Tooltip title="Download PDF">
              <span>
                <PDFDownloadLink
                  document={
                    <EstimatePDF
                      estimateData={estimateData}
                      estimateDetails={safeDetails}
                      companyData={finalCompanyData}
                    />
                  }
                  fileName={fileName}
                  style={{ textDecoration: 'none' }}
                >
                  {({ loading: dl }) => (
                    <IconButton size="small" disabled={dl} sx={{ color: '#059669' }}>
                      {dl ? <CircularProgress size={16} /> : <Download fontSize="small" />}
                    </IconButton>
                  )}
                </PDFDownloadLink>
              </span>
            </Tooltip>

            {/* Print */}
            <Tooltip title="Print">
              <IconButton size="small" onClick={handlePrint} disabled={loading} sx={{ color: '#7c3aed' }}>
                {loading ? <CircularProgress size={16} /> : <Print fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* WhatsApp */}
            <Tooltip title="Send via WhatsApp (PDF attached)">
              <IconButton
                size="small"
                onClick={handleWhatsApp}
                disabled={loading}
                sx={{
                  color: '#ffffff',
                  bgcolor: '#25D366',
                  width: 28, height: 28,
                  '&:hover': { bgcolor: '#1ebe5d' },
                  '&:disabled': { bgcolor: '#a7f3d0' }
                }}
              >
                {loading
                  ? <CircularProgress size={14} sx={{ color: 'white' }} />
                  : <WhatsApp sx={{ fontSize: 16 }} />
                }
              </IconButton>
            </Tooltip>

            {/* Close */}
            <IconButton size="small" onClick={onClose} sx={{ color: '#6b7280' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Error */}
        {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

        {/* ── PDF Viewer ────────────────────────────────────────────── */}
        <DialogContent sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? 'calc(100vh - 130px)' : '75vh'
        }}>
          <ErrorBoundary>
            <PDFViewer style={{ width: '100%', height: '100%', border: 'none' }}>
              <EstimatePDF
                estimateData={estimateData}
                estimateDetails={safeDetails}
                companyData={finalCompanyData}
              />
            </PDFViewer>
          </ErrorBoundary>
        </DialogContent>

        {/* ── Footer bar ────────────────────────────────────────────── */}
        <DialogActions sx={{
          bgcolor: '#eff6ff',
          borderTop: '1px solid #e5e7eb',
          py: 1, px: 2,
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Generated: {new Date().toLocaleString('en-IN')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* WhatsApp button in footer too */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleWhatsApp}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} /> : <WhatsApp />}
              sx={{
                color: '#25D366',
                borderColor: '#25D366',
                fontSize: '0.75rem',
                '&:hover': { bgcolor: '#f0fdf4', borderColor: '#1ebe5d' }
              }}
            >
              {loading ? 'Generating...' : 'WhatsApp'}
            </Button>

            <PDFDownloadLink
              document={
                <EstimatePDF
                  estimateData={estimateData}
                  estimateDetails={safeDetails}
                  companyData={finalCompanyData}
                />
              }
              fileName={fileName}
              style={{ textDecoration: 'none' }}
            >
              {({ loading: dl }) => (
                <Button
                  variant="contained"
                  size="small"
                  disabled={dl}
                  startIcon={dl ? <CircularProgress size={14} /> : <Download />}
                  sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#764ba2' }, fontSize: '0.75rem' }}
                >
                  {dl ? 'Preparing...' : 'Download PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EstimatePDFViewer;
